import React from 'react';
import {DialogTypes} from 'common/names';
import DialogComponent from 'components/Dialog';
import {useDispatch, useSelector} from 'react-redux';
import {Trans, useTranslation} from 'react-i18next';
import {ArchiveSettings, IArchiveSettings} from 'types/ArchiveSettings';
import {DiplomatFeature} from 'types/DiplomatFeature';
import {enums} from 'types/enums';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import Box from '@mui/material/Box';
import Panel from 'components/Panel';
import FormikDropdown from 'components/inputs/FormikDropdown';
import {settingsActions, settingsSelectors} from 'redux/settings';
import FormikCheckbox from 'components/inputs/FormikCheckbox';
import {licenseSelectors} from 'redux/license';
import LastUpdatedLabel from 'components/LastUpdatedLabel';
import FormikNumberField from 'components/inputs/FormikNumberField';
import FormikBrowseFileInput from 'components/inputs/FormikBrowseFileInput';
import FormikTextField from 'components/inputs/FormikTextField';
import Grid from '@mui/material/Grid';
import {ArchiveTriggerType} from 'types/diplomat/enums/ArchiveTriggerType';
import AsyncContainer from 'components/AsyncContainer';
import TestDirectory from 'features/layout/CommonPage/panels/TransportPanel/components/TestDirectory';
import {GridCentered} from 'components/GridCentered';
import {InputAdornment} from '@mui/material';
import Formik, {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import {useFieldValueGetter} from 'components/hook-form/useFieldValueGetter';

type Props = {
   onClose: () => void;
};

const ArchiveSettingsForm = ({onClose}: Props) => {
   const {t} = useTranslation();
   const isFeatureSupported = useSelector(licenseSelectors.isFeatureSupported);
   const getValue = useFieldValueGetter<IArchiveSettings>();

   const isSkipped = getValue('skip');
   const location = getValue('location');
   const deleted = getValue('delete');
   const timestamp = getValue('timestamp');
   const lastUser = getValue('lastUser');

   return (
      <>
         <FormikCheckbox autoFocus name="skip" label={t('Turn Off Primary Archiving')} />
         <Grid container>
            <Grid item md={11}>
               <Panel name={t('Primary Archive Directory')}>
                  <Grid container spacing={2}>
                     <Grid item xs={10}>
                        <FormikBrowseFileInput
                           name="location"
                           placeholder={t('Primary Location')}
                           disabled={isSkipped}
                           directoriesOnly
                        />
                     </Grid>
                     <GridCentered item xs={2}>
                        <TestDirectory disabled={isSkipped} directoryPath={location} />
                     </GridCentered>
                     <Grid item xs={12}>
                        <FormikCheckbox
                           name="useTransaction"
                           label={t('Add transaction-specific sub-directories')}
                           disabled={isSkipped}
                        />
                     </Grid>
                  </Grid>
               </Panel>
            </Grid>
         </Grid>
         <Panel name={t('Archive File Names')}>
            <FormikTextField name="sourceTemplate" label={t('Source Filename')} />
            <FormikTextField name="destinationTemplate" label={t('Destination Filename')} />
            <FormikTextField name="zipTemplate" label={t('Zip Filename')} />
            <FormikCheckbox name="overwrite" label={t('Do Not Overwrite')} inverse />
         </Panel>
         {isFeatureSupported(DiplomatFeature.ARCHIVE_FILE_CHOICE) ? (
            <Grid container spacing={2}>
               <Grid item md={6}>
                  <Panel name={t('Primary Archive File Selection')}>
                     <FormikDropdown
                        name="inboundFileType"
                        label={t('Inbound File Types')}
                        options={enums.archiveFileType.options}
                        disabled={isSkipped}
                     />
                     <FormikDropdown
                        name="outboundFileType"
                        label={t('Outbound File Types')}
                        options={enums.archiveFileType.options}
                        disabled={isSkipped}
                     />
                     <FormikCheckbox
                        name="zip"
                        label={t('Zip Archive Files')}
                        disabled={isSkipped}
                     />
                  </Panel>
               </Grid>
            </Grid>
         ) : null}

         <Grid container spacing={2}>
            <Grid item md={7}>
               <Panel name={t('Primary Archive Handling')}>
                  <FormikDropdown
                     name="trigger"
                     label={t('Attempt Archive on')}
                     options={ArchiveTriggerType.options}
                     disabled={isSkipped}
                  />
                  <FormikCheckbox
                     name="deleteSource"
                     label={t('Delete source files on primary or additional archive failure')}
                  />
               </Panel>
            </Grid>
            <Grid item md={5}>
               <Panel name={t('Primary Archive Retention')}>
                  <FormikCheckbox name="delete" label={t('Automatically Delete Archive Files')} />
                  <FormikNumberField
                     name="daysToKeep"
                     label={t('Retain Files For')}
                     disabled={!deleted}
                     InputProps={{
                        endAdornment: (
                           <InputAdornment position="end">
                              <Trans>Days</Trans>
                           </InputAdornment>
                        ),
                     }}
                  />
               </Panel>
            </Grid>
         </Grid>
         <LastUpdatedLabel date={timestamp} author={lastUser} />
         <Box textAlign="center">
            <FormFooterButtons cancel={onClose} submitLabel={t('Save')} />
         </Box>
      </>
   );
};

const ArchiveSettingsDialog = ({onClose}: Props) => {
   const archiveSettings = useSelector(settingsSelectors.getArchiveSettings);
   const isFetching = useSelector(settingsSelectors.isFetching);
   const dispatch = useDispatch();

   React.useEffect(() => {
      dispatch(settingsActions.getArchiveSettings);
   }, [dispatch]);

   const saveSettings = React.useCallback(
      (values: IArchiveSettings, helpers: FormOnSubmitHelpers<IArchiveSettings>) => {
         dispatch(
            settingsActions.postArchiveSettings(values, {
               onSuccess: onClose,
               onError: (errors: any) => {
                  helpers.setSubmitting(false);
                  helpers.setErrors(errors);
               },
            })
         );
      },
      [dispatch, onClose]
   );

   return (
      <DialogComponent
         maxWidth="md"
         header={DialogTypes.SettingsPrimaryArchive.dialogName}
         close={onClose}
      >
         <AsyncContainer loading={isFetching}>
            <Formik
               initialValues={ArchiveSettings.cast(archiveSettings) as IArchiveSettings}
               validationSchema={ArchiveSettings}
               onSubmit={saveSettings}
               enableReinitialize
            >
               <ArchiveSettingsForm onClose={onClose} />
            </Formik>
         </AsyncContainer>
      </DialogComponent>
   );
};

export default React.memo(ArchiveSettingsDialog);
